import type { Craftsman } from '../types';
import type { Company } from '../services/api/companyService';

/** 首頁推薦：過濾測試／占位名稱 */
export function isLowQualityDisplayName(name: string | undefined | null): boolean {
  const n = (name ?? '').trim();
  if (n.length < 2) return true;

  const lower = n.toLowerCase();
  if (/^(test|demo|asdf|qwerty|xxx+|abc+|123+|sample|null|undefined)$/.test(lower)) {
    return true;
  }
  if (/測試|假資料|占位|temp/i.test(n)) return true;

  const compact = n.replace(/\s/g, '');
  if (compact.length >= 4 && /^(.)\1+$/.test(compact)) return true;

  if (compact.length >= 4) {
    const uniqueChars = new Set(compact.toLowerCase()).size;
    if (uniqueChars <= 2) return true;
  }

  if (compact.length >= 5) {
    const freq = new Map<string, number>();
    for (const ch of compact) {
      freq.set(ch, (freq.get(ch) ?? 0) + 1);
    }
    const max = Math.max(...freq.values());
    if (max / compact.length >= 0.72) return true;
  }

  if (/^([a-z])\1{3,}$/i.test(compact)) return true;

  return false;
}

export function isCraftsmanEligibleForHome(c: Craftsman): boolean {
  if (isLowQualityDisplayName(c.name)) return false;
  const hasSpecialties = (c.specialties?.length ?? 0) > 0;
  const hasReviews = (c.numReviews ?? 0) > 0 || (c.rating ?? 0) > 0;
  const hasJobs = (c.completedJobs ?? 0) > 0;
  return hasSpecialties || hasReviews || hasJobs;
}

export function isCompanyEligibleForHome(co: Company): boolean {
  if (isLowQualityDisplayName(co.companyName)) return false;
  const hasServices = (co.services?.filter(Boolean).length ?? 0) > 0;
  const hasDesc = (co.description?.trim().length ?? 0) >= 8;
  const hasSocialProof = (co.totalReviews ?? 0) > 0 || (co.rating ?? 0) > 0;
  const hasJobs = (co.completedJobs ?? 0) > 0;
  return hasServices || hasDesc || hasSocialProof || hasJobs;
}

export function filterHomeRecommendedCraftsmen(list: Craftsman[]): Craftsman[] {
  return list.filter(isCraftsmanEligibleForHome);
}

export function filterHomeRecommendedCompanies(list: Company[]): Company[] {
  return list.filter(isCompanyEligibleForHome);
}

export function distanceKmBetween(
  from: { lat: number; lng: number } | null | undefined,
  to: { lat: number; lng: number } | null | undefined
): number | null {
  if (!from || !to) return null;
  if (!Number.isFinite(from.lat) || !Number.isFinite(from.lng)) return null;
  if (!Number.isFinite(to.lat) || !Number.isFinite(to.lng)) return null;

  const R = 6371;
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLon = ((to.lng - from.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((from.lat * Math.PI) / 180) *
      Math.cos((to.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function formatDistanceKm(km: number): string {
  if (km < 1) return `${Math.max(100, Math.round(km * 1000))} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}
